import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { quotesAPI, customersAPI, pricingAPI, usersAPI } from '../services/api';
import { fmt, STATUS_LABELS, ALERT_META, FAMILY_LABELS, NO_FAMILY, otherFamily } from '../utils/format';
import { crossFamilyText } from '../utils/crossFamily';
import Sidebar from '../components/layout/Sidebar';
import AluLineModal from '../components/quotes/AluLineModal';
import IronLineModal from '../components/quotes/IronLineModal';
import StatusModal from '../components/quotes/StatusModal';
import SiteConditionsCard from '../components/quotes/SiteConditionsCard';
import CustomerProjectCard from '../components/quotes/CustomerProjectCard';
import CostSummaryCard from '../components/quotes/CostSummaryCard';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function QuoteDetailPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { isManager } = useAuth();

  const [quote, setQuote]           = useState(null);
  const [loading, setLoading]        = useState(true);
  const [showAluModal, setAluModal]  = useState(false);
  const [showIronModal, setIronModal]= useState(false);
  const [showStatus, setShowStatus]  = useState(false);
  const [editLine, setEditLine]      = useState(null);
  const [sellers, setSellers]        = useState([]);
  const [crossAck, setCrossAck]      = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await quotesAPI.get(id);
      setQuote(data);
    } catch { toast.error('Quote not found'); navigate('/quotes'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Selector de vendedor: solo manager y admin. El vendedor define la tarifa
  // de comision, asi que Melissa cotiza a nombre de quien vendio.
  useEffect(() => {
    if (!isManager) return;
    usersAPI.salespeople().then(r => setSellers(r.data)).catch(() => {});
  }, [isManager]);

  const handleDeleteAlu = async (lineId) => {
    if (!window.confirm('Delete this line?')) return;
    await quotesAPI.deleteAluLine(id, lineId);
    toast.success('Line deleted');
    load();
  };

  const handleDeleteIron = async (lineId) => {
    if (!window.confirm('Delete this line?')) return;
    await quotesAPI.deleteIronLine(id, lineId);
    toast.success('Line deleted');
    load();
  };

  const handleDuplicate = async () => {
    const { data } = await quotesAPI.duplicate(id);
    toast.success(`Created ${data.quote_number}`);
    navigate(`/quotes/${data.id}`);
  };

  // Paso 3 familia (21-oct-2026): cambiar la familia del trabajo (con confirmacion y 409 de piezas cruzadas).
  const setFamily = async (job_family) => {
    // Poner familia desde el banner ya es una decision explicita: solo se confirma un CAMBIO.
    if (quote.job_family && !window.confirm(
      `Change job type from ${FAMILY_LABELS[quote.job_family].label} to ${FAMILY_LABELS[job_family].label}?\n\nThe whole quote will be commissioned as ${FAMILY_LABELS[job_family].label}.`)) return;
    try {
      try {
        await quotesAPI.update(id, { job_family });
      } catch (err) {
        const d = err.response && err.response.data;
        if (!err.response || err.response.status !== 409 || !d || d.code !== 'CROSS_FAMILY') throw err;
        if (!window.confirm(d.error)) return;
        await quotesAPI.update(id, { job_family, cross_family_ack: true });
      }
      toast.success(`Job type: ${FAMILY_LABELS[job_family].label}`);
      load();
    } catch (err) { toast.error((err.response && err.response.data && err.response.data.error) || 'Could not change job type'); }
  };

  // Una sola puerta para agregar o duplicar un renglon: aqui vive el aviso de pieza cruzada.
  // Editar (lapiz) no pasa por aqui: la pieza ya fue aceptada.
  const openAdd = (lineFamily, preset = null) => {
    const cross = !!quote.job_family && quote.job_family !== lineFamily;
    if (cross && !window.confirm(crossFamilyText(quote.job_family, lineFamily))) return;
    setCrossAck(cross);
    setEditLine(preset);
    if (lineFamily === 'alu') setAluModal(true); else setIronModal(true);
  };

  const handleReopen = async () => {
    if (!window.confirm('Reopen this quote? It will be moved back to Open status.')) return;
    try {
      await quotesAPI.updateStatus(id, { status: 'open' });
      toast.success('Quote reopened');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reopen failed');
    }
  };

  const downloadPdf = () => {
    // Aviso si faltan datos obligatorios: el PDF sale marcado DRAFT.
    const missing = [];
    if (!quote?.customer_id) missing.push('customer');
    if (!quote?.install_street || !String(quote.install_street).trim()) missing.push('street address');
    if (!quote?.install_city || !String(quote.install_city).trim()) missing.push('city');
    if (missing.length && !window.confirm(
      `This quote is incomplete (missing: ${missing.join(', ')}).\n\n` +
      `The PDF will be watermarked DRAFT and is not for client delivery.\n\nDownload anyway?`)) return;

    const token = localStorage.getItem('mq_token');
    const url   = `/api/quotes/${id}/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Quote_${quote.quote_number}.pdf`;
        a.click();
      });
  };

  if (loading) return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content"><div className="loading-center"><div className="spinner" /></div></div>
    </div>
  );

  if (!quote) return null;
  const st = STATUS_LABELS[quote.status] || STATUS_LABELS.draft;
  const aluLines  = quote.lines_alu  || [];
  const ironLines = quote.lines_iron || [];
  const installer = quote.installer || 'buiria';
  const jobFamily = quote.job_family || null;
  const fam       = FAMILY_LABELS[jobFamily] || NO_FAMILY;
  const hideAlu   = jobFamily === 'iron' && aluLines.length === 0;
  const hideIron  = jobFamily === 'alu'  && ironLines.length === 0;

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        {/* Header */}
        <div className="page-header">
          <div>
            <div className="flex items-center gap-2">
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/quotes')}>← Back</button>
              <h2 className="text-mono">{quote.quote_number}</h2>
              {quote.version > 1 && <span className="badge badge-gray">v{quote.version}</span>}
              <span className={`badge ${st.cls}`}>{st.label}</span>
              <span className={`badge ${fam.cls}`} style={{ fontSize: 12 }}>{fam.label}</span>
              {jobFamily && isManager && !['closed_won', 'closed_lost'].includes(quote.status) && (
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                  onClick={() => setFamily(otherFamily(jobFamily))}>Change</button>
              )}
            </div>
            <div className="page-subtitle" style={{ marginTop: 4 }}>
              {quote.customer_name || 'No customer'} · {quote.salesperson_name} · {fmt.date(quote.created_at)}
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" onClick={handleDuplicate}>⧉ Duplicate</button>
            <button className="btn btn-ghost btn-sm" onClick={downloadPdf}>↓ PDF</button>
            {(quote.status === 'closed_won' || quote.status === 'closed_lost') && (
              <button className="btn btn-primary btn-sm" onClick={handleReopen}>&#x27F2; Reopen</button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setShowStatus(true)}>Update Status</button>
          </div>
        </div>

        <div className="page-body">
          {!jobFamily && (
            <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--orange)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>This quote has no job type</div>
              <div className="text-muted text-sm" style={{ marginBottom: 10 }}>
                Pick one: everything in the quote will be commissioned as that type.
              </div>
              <div className="flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={() => setFamily('iron')}>Iron</button>
                <button className="btn btn-primary btn-sm" onClick={() => setFamily('alu')}>Aluminum</button>
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>

            {/* MAIN — Lines */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Installation — manager ve quien instala; vendedor solo on/off */}
              <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Installation:</span>
                {isManager ? (
                  <>
                    {installer === 'buiria'   && <span className="badge badge-blue">Buiria</span>}
                    {installer === 'magnolia' && <span className="badge badge-green">AMS</span>}
                    {installer === 'none'     && <span className="badge badge-gray">No installation</span>}
                    <select
                      className="form-select"
                      style={{ width: 180, fontSize: 12 }}
                      value={installer}
                      onChange={async e => {
                        try {
                          await fetch(`/api/quotes/${id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type':'application/json', Authorization: `Bearer ${localStorage.getItem('mq_token')}` },
                            body: JSON.stringify({ installer: e.target.value }),
                          });
                          load();
                        } catch {}
                      }}
                    >
                      <option value="buiria">Buiria</option>
                      <option value="magnolia">AMS</option>
                      <option value="none">No installation</option>
                    </select>
                  </>
                ) : (
                  <>
                    <span className={`badge ${installer === 'none' ? 'badge-gray' : 'badge-blue'}`}>
                      {installer === 'none' ? 'No installation' : 'With installation'}
                    </span>
                    <select
                      className="form-select"
                      style={{ width: 180, fontSize: 12 }}
                      value={installer === 'none' ? 'none' : 'with'}
                      onChange={async e => {
                        const val = e.target.value === 'none'
                          ? 'none'
                          : (installer === 'magnolia' ? 'magnolia' : 'buiria');
                        try {
                          await fetch(`/api/quotes/${id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type':'application/json', Authorization: `Bearer ${localStorage.getItem('mq_token')}` },
                            body: JSON.stringify({ installer: val }),
                          });
                          load();
                        } catch {}
                      }}
                    >
                      <option value="with">With installation</option>
                      <option value="none">No installation</option>
                    </select>
                  </>
                )}
              </div>

          {/* Tipo de proyecto + construccion (afecta tax sobre labor) */}
          <div className="flex items-center gap-2" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Project:</span>
            <select
              className="form-select"
              style={{ width: 150, fontSize: 12 }}
              value={quote.project_type || 'residential'}
              onChange={async e => {
                try {
                  await fetch(`/api/quotes/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type':'application/json', Authorization: `Bearer ${localStorage.getItem('mq_token')}` },
                    body: JSON.stringify({ project_type: e.target.value }),
                  });
                  load();
                } catch {}
              }}
            >
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
            </select>
            <select
              className="form-select"
              style={{ width: 180, fontSize: 12 }}
              value={quote.construction_type || 'new'}
              onChange={async e => {
                try {
                  await fetch(`/api/quotes/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type':'application/json', Authorization: `Bearer ${localStorage.getItem('mq_token')}` },
                    body: JSON.stringify({ construction_type: e.target.value }),
                  });
                  load();
                } catch {}
              }}
            >
              <option value="new">New construction</option>
              <option value="remodel">Remodel</option>
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Odoo SO:</span>
            <input
              className="form-input text-mono"
              style={{ width: 130, fontSize: 12 }}
              placeholder="SO12987"
              key={quote.odoo_so_number || 'so'}
              defaultValue={quote.odoo_so_number || ''}
              onBlur={async e => {
                const v = e.target.value.trim();
                if (v === (quote.odoo_so_number || '')) return;
                try {
                  await quotesAPI.update(id, { odoo_so_number: v });
                  load();
                } catch { toast.error('Could not save the Odoo SO'); }
              }}
            />
            {isManager && sellers.length > 0 && (
              <>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Salesperson:</span>
                <select
                  className="form-select"
                  style={{ width: 180, fontSize: 12 }}
                  value={quote.salesperson_id || ''}
                  onChange={async e => {
                    try {
                      await quotesAPI.update(id, { salesperson_id: parseInt(e.target.value, 10) });
                      load();
                    } catch { toast.error('Could not change salesperson'); }
                  }}
                >
                  {sellers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </>
            )}
            {quote.project_type === 'commercial' && quote.construction_type === 'remodel' && (
              <span className="badge badge-blue" style={{ fontSize: 11 }}>Labor con tax</span>
            )}
          </div>

          {/* ALU LINES */}
          <div style={{ order: jobFamily === 'iron' ? 2 : 1 }}>
          {hideAlu ? (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, marginBottom: 16 }}
              onClick={() => openAdd('alu')}>+ Add aluminum piece (other job type)</button>
          ) : (<>
              <div className="flex items-center justify-between mb-3">
                <h3 style={{ fontSize: 14, fontWeight: 700 }}>Aluminum Lines</h3>
                <button className="btn btn-primary btn-sm" onClick={() => openAdd('alu')}>
                  + Add Aluminum
                </button>
              </div>

              {aluLines.length === 0 ? (
                <div className="card" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13, marginBottom: 20 }}>
                  No aluminum lines yet
                </div>
              ) : (
                <div style={{ marginBottom: 20 }}>
                  {aluLines.map(l => {
                    const alertMeta = l.alert ? ALERT_META[l.alert] : null;
                    const noPrice = l.net_sales == null || parseFloat(l.net_sales) === 0;
                    // Conflicto installer (candado b, decision 2026-06-06): no se
                    // auto-corrige, se marca para editar — el flag toca dinero.
                    const mismatch =
                      (l.include_buiria && installer !== 'buiria' && 'Buiria install — quote installer is not Buiria') ||
                      (l.include_magnolia_labor && installer !== 'magnolia' && 'Magnolia labor — quote installer is not Magnolia') ||
                      null;
                    return (
                      <div key={l.id} className={`quote-line-card${l.alert === 'LOW' ? ' alert-low-border' : l.alert === 'HIGH' ? ' alert-high-border' : ''}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-mono" style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 700, minWidth: 36 }}>
                              {l.line_number}
                            </span>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {l.model_code} — {l.product_desc || l.location || '—'}
                              </div>
                              <div className="text-muted text-sm">
                                {l.width_in}"×{l.height_in}" · {l.qty} unit{l.qty > 1 ? 's' : ''} · {fmt.sqft(l.total_sqft)}
                                {l.location && ` · ${l.location}`}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {noPrice && <span className="badge badge-red">&#9888; No price</span>}
                            {alertMeta && <span className={alertMeta.cls}>{alertMeta.label}</span>}
                            <div className="text-right">
                              <div className="price-net">{noPrice ? '$0.00' : fmt.currency(l.net_sales)}</div>
                              <div className="text-muted text-sm">{l.price_sf ? `${fmt.currency(l.price_sf)}/sf` : '—'}</div>
                            </div>
                            <button className="btn btn-ghost btn-icon btn-sm"
                              onClick={() => openAdd('alu', { ...l, id: undefined })}>⧉</button>
                              <button className="btn btn-ghost btn-icon btn-sm"
                              onClick={() => { setEditLine(l); setAluModal(true); }}>✎</button>
                            <button className="btn btn-danger btn-icon btn-sm"
                              onClick={() => handleDeleteAlu(l.id)}>✕</button>
                          </div>
                        </div>
                        {mismatch && (
                          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>
                            &#9888; {mismatch} — edit the line or the quote installer
                          </div>
                        )}
                        {l.include_buiria && l.buiria_inst_total > 0 && (
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-3)' }}>
                            + Installation: <span style={{ color: 'var(--text-2)' }}>{fmt.currency(l.buiria_inst_total)}</span>
                          </div>
                        )}
                        {/* Preliminar: visible para TODOS (incl. Vendedor) hasta que planta confirme. */}
                        {!l.factory_quoted && (
                          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>
                            Preliminary - subject to plant quote
                          </div>
                        )}
                        {isManager && l.rec_sf && (
                          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
                            Recommended: {fmt.currency(l.rec_sf)}/sf
                            {l.ddp_unit && l.sqft_unit ? ` · Cost: ${fmt.currency(l.ddp_unit / l.sqft_unit)}/sf` : ''}
                            {l.actual_cm_pct ? ` · CM: ${fmt.percent(l.actual_cm_pct)}` : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

          </>)}
          </div>
              {/* IRON LINES */}
          <div style={{ order: jobFamily === 'iron' ? 1 : 2, marginBottom: jobFamily === 'iron' ? 20 : 0 }}>
          {hideIron ? (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, marginBottom: 16 }}
              onClick={() => openAdd('iron')}>+ Add iron piece (other job type)</button>
          ) : (<>
              <div className="flex items-center justify-between mb-3">
                <h3 style={{ fontSize: 14, fontWeight: 700 }}>Iron Door Lines</h3>
                <button className="btn btn-primary btn-sm" onClick={() => openAdd('iron')}>
                  + Add Iron Door
                </button>
              </div>

              {ironLines.length === 0 ? (
                <div className="card" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                  No iron door lines yet
                </div>
              ) : (
                <div>
                  {ironLines.map(l => (
                    <div key={l.id} className="quote-line-card">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-mono" style={{ color: 'var(--bronze)', fontSize: 12, fontWeight: 700, minWidth: 36 }}>
                            {l.line_number}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {l.category_name} — {l.design_name}
                            </div>
                            <div className="text-muted text-sm">
                              {l.width_in}"×{l.height_in}" · {fmt.sqft(l.sqft)}
                              {l.location && ` · ${l.location}`}
                              {l.is_remodel && ' · Remodel'}
                              {l.include_labor && ' · w/Labor'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            {/* Precio CLIENTE estimado (post-NP). El costo interno
                                solo existe en la respuesta para manager/admin. */}
                            <div className="price-net">{fmt.currency(l.customer_price)}</div>
                            <div className="text-muted text-sm">
                              {l.customer_price_sf != null ? `${fmt.currency(l.customer_price_sf)}/sf` : '—'}
                            </div>
                            {isManager && l.line_total != null && (
                              <div style={{ fontSize: 10, color: 'var(--text-3)' }}>
                                cost {fmt.currency(l.line_total)}
                              </div>
                            )}
                          </div>
                          <button className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => openAdd('iron', { ...l, id: undefined })}>⧉</button>
                            <button className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => { setEditLine(l); setIronModal(true); }}>✎</button>
                          <button className="btn btn-danger btn-icon btn-sm"
                            onClick={() => handleDeleteIron(l.id)}>✕</button>
                        </div>
                      </div>
                      {l.include_labor && installer === 'none' && (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>
                          &#9888; Line includes labor but quote installer is "No installation" — edit the line or the quote installer
                        </div>
                      )}
                      {/* Glass info */}
                      {l.glass && l.glass.length > 0 && l.glass.map(g => (
                        <div key={g.id} style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-3)' }}>
                          Glass: {g.glass_type_name} · {g.thickness_label} · {g.cuts} cuts
                          {g.is_curved && ' · Curved'} · {fmt.sqft(g.sqft)}
                          <span style={{ color: 'var(--text-2)', marginLeft: 8 }}>
                            {g.customer_price != null ? fmt.currency(g.customer_price) : ''}
                          </span>
                          {g.has_limited_warranty && <span className="badge badge-orange" style={{ marginLeft: 6 }}>Limited Warranty</span>}
                        </div>
                      ))}
                      {isManager && l.labor_hours > 0 && (
                        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-3)' }}>
                          Labor: {l.labor_persons} crew · {l.labor_hours}h
                          {l.labor_total != null && (
                            <span> = <span style={{ color: 'var(--text-2)' }}>{fmt.currency(l.labor_total)} cost</span></span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </>)}
          </div>
            </div>

            {/* SIDEBAR — Totals */}
            <div style={{ position: 'sticky', top: 80 }}>
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Quote Summary</span>
                </div>

                {/* Customer + Project + Installation address */}
                <CustomerProjectCard quote={quote} quoteId={id} onSaved={load} />

                <hr className="divider" />

                {/* Totals — desglose v3 PERSISTIDO (updateQuoteTotals).
                    Fuente unica de verdad: mismos campos que el SO de Odoo
                    y el PDF. Aqui NO se recalcula nada. */}
                {(() => {
                  const laborTaxed = quote.project_type === 'commercial'
                                  && quote.construction_type === 'remodel';
                  const sales  = parseFloat(quote.sales_subtotal || 0);
                  const labor  = parseFloat(quote.labor_subtotal || 0);
                  const taxAmt = parseFloat(quote.tax_amount || 0);
                  const taxPct = parseFloat(quote.tax_pct || 0);
                  return (
                <div style={{ fontSize: 13 }}>
                  <TotalRow label="Sales" value={sales} />
                  {labor > 0 && (
                    <TotalRow label="Labor" value={labor}
                      note={laborTaxed ? 'taxed' : 'no tax'} />
                  )}
                  <hr className="divider" style={{ margin: '6px 0' }} />
                  <TotalRow label="Subtotal" value={sales + labor} note="= sum of lines" />
                  {taxPct > 0 ? (
                    <TotalRow
                      label={`Tax (${(taxPct * 100).toFixed(2)}%)`}
                      value={taxAmt}
                      note={laborTaxed ? 'sales + labor' : 'sales only'}
                    />
                  ) : (
                    <div style={{ fontSize: 11, color: 'var(--green)', marginBottom: 8 }}>&#10003; Tax Exempt</div>
                  )}
                  <hr className="divider" />
                  <div className="flex justify-between items-center">
                    <span style={{ fontWeight: 700 }}>TOTAL</span>
                    <span className="price-total">{fmt.currency(quote.grand_total)}</span>
                  </div>
                  {isManager && parseFloat(quote.expenses_total || 0) > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 6 }}>
                      Includes expenses {fmt.currency(quote.expenses_total)}
                      {parseFloat(quote.transport_fee || 0) > 0 && ` · transport ${fmt.currency(quote.transport_fee)}`}
                    </div>
                  )}
                  {isManager && (
                    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>Commission</div>
                      {(parseFloat(quote.commission_pct || 0) === 0 && (ironLines.length > 0 || aluLines.length > 0)) ? (
                        <div style={{ fontSize: 11, color: 'var(--red)' }}>
                          ⚠ Commission not configured for this salesperson
                        </div>
                      ) : (
                        <div className="flex justify-between items-center" style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          <span>{quote.salesperson_name} · {(parseFloat(quote.commission_pct || 0) * 100).toFixed(0)}% on {fmt.currency(quote.commission_base)}</span>
                          <span style={{ color: 'var(--text-2)' }}>{fmt.currency(quote.commission_amount)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <CostSummaryCard quoteId={quote.id} />
                  {!laborTaxed && labor > 0 && taxPct > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 6 }}>
                      * Labor not subject to sales tax
                    </div>
                  )}
                  {laborTaxed && taxPct > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 6 }}>
                      * Commercial remodel — labor is taxed (installation still exempt)
                    </div>
                  )}
                </div>
                  );
                })()}

                <div style={{ marginTop: 16 }}>
                  <button className="btn btn-ghost w-full btn-sm" onClick={downloadPdf}>
                    ↓ Download PDF
                  </button>
                </div>
              </div>

              {/* Site Conditions */}
              <SiteConditionsCard
                quoteId={id}
                value={quote}
                onSaved={load}
              />

              {/* Notes */}
              {quote.notes && (
                <div className="card" style={{ marginTop: 12 }}>
                  <div className="card-title" style={{ marginBottom: 8 }}>Notes</div>
                  <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.7 }}>{quote.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAluModal && (
        <AluLineModal
          quoteId={id}
          editData={editLine}
          installer={installer}
          crossFamilyAck={crossAck}
          onClose={() => { setAluModal(false); setEditLine(null); setCrossAck(false); }}
          onSaved={() => { setAluModal(false); setEditLine(null); setCrossAck(false); load(); }}
        />
      )}
      {showIronModal && (
        <IronLineModal
          quoteId={id}
          installer={installer}
          editData={editLine}
          crossFamilyAck={crossAck}
          onClose={() => { setIronModal(false); setEditLine(null); setCrossAck(false); }}
          onSaved={() => { setIronModal(false); setEditLine(null); setCrossAck(false); load(); }}
        />
      )}
      {showStatus && (
        <StatusModal
          quote={quote}
          onClose={() => setShowStatus(false)}
          onSaved={() => { setShowStatus(false); load(); }}
        />
      )}
    </div>
  );
}

function TotalRow({ label, value, note }) {
  return (
    <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
      <span style={{ color: 'var(--text-2)' }}>
        {label}
        {note && <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 4 }}>({note})</span>}
      </span>
      <span className="text-mono">{fmt.currency(value)}</span>
    </div>
  );
}
